import { useState } from "react";
import { HOUR_STEPS } from "@riseafrica/scoring";
import type { DiagnosticResponse } from "../lib/api";
import { chipClass, libelleNiveau } from "../lib/format";
import { useApp } from "../state/AppState";
import { Fil } from "../components/Fil";

interface Props {
  pack: DiagnosticResponse;
  onScore: (heuresParTache: Record<number, number>) => void;
  onChangerMetier: () => void;
}

/**
 * Volet 01 — temps déclaré par tâche. C'est cette pondération qui rend
 * l'indice difficile à manipuler et comparable entre profils.
 */
export function Tasks({ pack, onScore, onChangerMetier }: Props) {
  const { say } = useApp();
  const [heures, setHeures] = useState<Record<number, number>>({});
  const [insuffisant, setInsuffisant] = useState(false);

  const choisies = Object.values(heures).filter((h) => h > 0).length;

  const valider = () => {
    if (choisies < 2) {
      setInsuffisant(true);
      say("Renseignez au moins deux tâches");
      return;
    }
    setInsuffisant(false);
    onScore(heures);
  };

  return (
    <section>
      <div className="shell narrow app">
        <Fil etape={1} />
        <h2>{pack.metier}</h2>

        {pack.degraded && (
          <div className="warn">
            Résultat approximatif : notre outil n'a pas répondu. Voici des tâches courantes d'un
            métier voisin du vôtre, pas celles de votre métier. Réessayez dans un moment pour un
            résultat propre à votre métier.
          </div>
        )}

        <p className="sub">
          {pack.degraded
            ? "Voici des tâches courantes d'un métier voisin du vôtre, déjà classées."
            : `Voici ce qui compose ce métier, déjà classé tâche par tâche. Reconnaissez-vous votre semaine ?`}
        </p>

        {/*
          Une phrase avant les tâches, pas huit — 07/10/2026.

          La consigne tenait huit lignes et la légende deux : sur téléphone,
          elles repoussaient les boutons de la première tâche au bord de
          l'écran, et ce jour-là sept diagnostics lancés sur neuf s'arrêtaient
          avant l'indice. Ce qu'il faut pour agir reste ici ; ce qui rassure et
          ce qui explique passe sous la liste, où on le lit si l'on en a besoin.
        */}
        <div className="guide">
          <b>Indiquez le temps</b> que chaque tâche vous prend par semaine — deux suffisent.
        </div>

        <div
          role="group"
          aria-label="Temps passé par tâche"
          style={insuffisant ? { outline: "2px solid var(--high)" } : undefined}
        >
          {pack.taches.map((tache, i) => (
            <div className={`tk${heures[i] !== undefined ? " set" : ""}`} key={`${tache.texte}-${i}`}>
              {/*
                Le classement est montré ici, et non plus seulement au résultat.
                Il n'était visible qu'après huit sélections de durée : le seul
                moment où le produit peut démontrer qu'il connaît le métier était
                dépensé à faire travailler la personne avant de lui avoir rien
                donné. La pondération par le temps déclaré ne change pas.
              */}
              <div className="top">
                <div className="lbl" id={`tkl${i}`}>
                  {tache.texte}
                </div>
                <i className={chipClass(tache.niveau)}>{libelleNiveau(tache.niveau)}</i>
              </div>
              <div className="seg" role="group" aria-labelledby={`tkl${i}`}>
                {HOUR_STEPS.map((pas) => (
                  <button
                    key={pas.libelle}
                    type="button"
                    aria-pressed={heures[i] === pas.heures}
                    onClick={() => setHeures((h) => ({ ...h, [i]: pas.heures }))}
                  >
                    {pas.libelle}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {insuffisant && (
          <div className="err">
            Renseignez le temps passé sur au moins deux tâches : l'indice se calcule sur cette
            pondération.
          </div>
        )}

        {/* Laiton comme sur les écrans de résultat : le geste qui fait avancer
              dans le parcours doit avoir le même poids partout. Il était
              secondaire sur les trois écrans de saisie et primaire sur les
              trois écrans de résultat — la même intention changeait de rang
              d'un écran à l'autre. */}
        {/* Collé au bas de l'écran — 07/10/2026 : il était sous la dernière
              tâche, trois écrans plus bas que les deux qu'il suffit de remplir. */}
        <button type="button" className="btn btn-brass btn-colle" onClick={valider}>
          {choisies < 2 ? `Voir mon indice — ${2 - choisies} tâche${choisies === 1 ? "" : "s"} de plus` : "Voir mon indice"}
        </button>

        <p className="fine" id="legende-niveaux">
          <b>Automatisable</b> : une IA le fait déjà. <b>Partiel</b> : en partie, avec votre
          arbitrage. <b>Résiste</b> : cela reste à vous.
        </p>
        <p className="fine">
          Davantage de tâches renseignées affine le résultat. Il n'y a pas de bonne réponse : un
          score élevé n'est ni un échec ni un jugement, c'est une information sur votre métier, pas
          sur vous. Le temps déclaré pondère le calcul, et c'est ce qui rend votre indice comparable
          à celui de vos confrères.
        </p>
        <button type="button" className="back" onClick={onChangerMetier}>
          ← Changer de métier
        </button>
      </div>
    </section>
  );
}
